<template>
<div class="surveillance-edit-modal">
  <modal v-model="editModal"
         size="lg"
         :title="`${surveillance.establishment.name} - SPIADI ${surveillance.year}`">
    <div v-if="editModal"
         class="edit-modal">
      <h3>Modules</h3>
      <div class="row">
        <div v-for="p in allProtocols"
             class="col-xs-4 protocols"
             :key="`protocol-${p.type}`">
          <c-checkbox :type="p.type" v-model="protocols[p.type]" block>
            {{ p.label }}
          </c-checkbox>
        </div>
      </div>
      <transition name="fade">
        <collapse v-model="showUfs">
          <div class="uf-collapse-content">
            <hr />
            <h3>Liste des Ufs</h3>
            <div class="row">
              <transition-group name="fade" tag="div">
                <div v-for="(p, i) in availableProtocolsWithUfs"
                     :key="`uf-protocol-${p.type}`">
                  <div class="col-xs-6" v-if="p.component == null">
                    <unit-list v-model="p.units"
                               :type="p.type"
                               :title="p.label"
                               :typeahead="p.typeahead"
                               :unitTypes="p.unitTypes"></unit-list>
                  </div>
                  <div class="col-xs-6" v-else>
                    <component :is="p.component"
                               v-model="p.units"
                               :config="p.config">
                    </component>
                  </div>
                  <div class="clearfix" v-if="i % 2 === 1"></div>
                </div>
              </transition-group>
            </div>
          </div>
        </collapse>
      </transition>
      <collapse v-model="error.error">
        <div class="error-collapse-content">
          <hr>
          <sui-error-panel :error="error">
          </sui-error-panel>
        </div>
      </collapse>
    </div>
    <template slot="footer">
      <btn type="success" :disabled="!modified" @click="save" size="lg">
        Enregistrer les modifications
      </btn>
    </template>
  </modal>
</div>
</template>

<script>
import unitList from '@/sui/components/unitlist'
import selectUnit from '@/sui/components/selectunit'
import checkbox from '@/sui/components/checkbox'
import suiErrorPanel from '@/sui/components/errorpanel'
import Error from '@/sui/tools/error'

export default {
  name: 'surveillance-edit-modal',

  props: {
    surveillance: Object,

    event: {
      type: String,
      required: true
    },

    value: {
      type: Boolean,
      required: true
    },

    moduleList: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      editModal: this.value,
      protocols: null,
      units: null,
      error: new Error(),
      waiting: false
    }
  },

  computed: {
    showUfs: function () {
      return this.availableProtocolsWithUfs.length > 0
    },

    availableProtocolsWithUfs: function () {
      const result = []
      for (let p in this.protocols) {
        const units = this.units[p]
        if (this.protocols[p] && units != null) {
          const mod = this.moduleList[p]
          if (mod.units.component != null) {
            result.push(
              {
                component: mod.units.component,
                units: units,
                label: mod.label,
                config: mod.units.config(mod, this.surveillance.modules)
              }
            )
          } else {
            const types = mod.units
            result.push(
              {
                type: p,
                label: mod.label,
                units: units,
                typeahead: types.length > 20,
                unitTypes: types
              }
            )
          }
        }
      }
      return result
    },

    allProtocols: function () {
      const result = []
      if (this.protocols == null) {
        return result
      }

      const list = this.moduleList
      for (let m in list) {
        const mod = list[m]
        if (mod.sheets != null) {
          result.push(
            {
              type: m,
              label: mod.label,
              class: `label label-${m}`
            }
          )
        }
      }
      return result
    },

    modified: function () {
      return this.modalitiesModified && this.valid
    },

    modalitiesModified: function () {
      this.error.reset()
      const different = (f, s) => {
        if ((f == null) !== (s == null)) {
          return true
        }

        if (f.length !== s.length) {
          return true
        }

        for (let i in f) {
          const b = f[i]
          const same = (a) => a.ufType === b.ufType && a.name === b.name
          if (s.find(same) == null) {
            return true
          }
        }
        return false
      }

      const f = this.surveillance.modules
      const s = this.protocols
      const u = this.units
      for (let p in s) {
        const mod = f[p]
        const exists = mod != null
        if (s[p] !== exists) {
          return true
        }

        if (exists) {
          const ufs = this.surveillance.getUfs(p)

          if (ufs != null && different(u[p], ufs)) {
            return true
          }
        }
      }

      return false
    },

    valid: function () {
      this.error.reset()

      let allUnits = []
      let duplicates = []
      for (let p in this.availableProtocolsWithUfs) {
        const prot = this.availableProtocolsWithUfs[p]
        if (prot.units.length === 0) {
          if (p === 'reaSink') {
            this.setInvalid(
              'UF manquante',
              `Vous devez sélectionner une UF dans ${prot.label}.`
            )
          } else {
            this.setInvalid(
              'UF manquante',
              `Il doit y avoir au moins une UF dans ${prot.label}.`
            )
          }
          return false
        } else if (prot.component == null) {
          for (let i in prot.units) {
            const u = prot.units[i]
            const same = (a) => a.type === u.type && a.name === u.name
            if (allUnits.find(same)) {
              duplicates.push(`${u.name} (${prot.label})`)
            } else {
              allUnits.push(u)
            }
          }
        }
      }

      if (duplicates.length > 0) {
        this.setInvalid(
          `Doublon d'UF`,
          `Certaines ufs sont déclarées plusieurs fois, merci
de ne laisser qu'un seul exemplaire de chaque : ${duplicates.join(', ')}.`
        )
        return false
      }
      return true
    }
  },

  watch: {
    editModal: {
      handler () {
        this.$emit('input', this.editModal)
        if (!this.editModal) {
          this.refresh()
        }
      },
      deep: true
    },

    value: function (value) {
      this.editModal = value
    },

    surveillance: function () {
      this.refresh()
    },

    moduleList: function () {
      this.refresh()
    }
  },

  methods: {
    refresh: function () {
      const smod = this.surveillance.modules
      const list = this.moduleList
      const protocols = {}
      const units = {}
      for (let p in list) {
        const mod = list[p]
        if (mod.sheets != null) {
          protocols[p] = smod[p] != null
          const ufs = this.surveillance.getUfs(p)
          if (ufs != null) {
            units[p] = ufs
          } else if (mod.units) {
            units[p] = []
          }
        }
      }
      this.protocols = protocols
      this.units = units
    },

    setInvalid: function (heading, text) {
      this.error.set(
        'Configuration invalide !',
        heading,
        text,
        false
      )
    },

    setError: function () {
      this.error.set(
        'Erreur interne !',
        `Une erreur inattendue s'est produite lors du choix des modules.`,
        `Vérifiez votre connexion internet avant de réessayer.`,
        true
      )
    },

    save: function () {
      this.error.reset()

      const u = {}
      for (let p in this.protocols) {
        if (this.protocols[p]) {
          u[p] = this.units[p]
        }
      }

      const request = {
        target: this.surveillance,
        protocols: this.protocols,
        units: u
      }
      const vm = this
      this.$store.dispatch(this.event, request).then(
        (response) => {
          const { err } = response.data
          if (err != null) {
            if (err === '[ModuleReaSinkStatus]') {
              this.error.set(
                'Modification interdite !',
                `Les écouvillons ayant été envoyés, il n'est plus possible de
modifier l'inscription au module RÉA Sink.`,
                `Si le service indiqué n'est pas le bon, merci de nous
contacter en utilisant le formulaire de contact.`,
                false
              )
            } else if (err === '[ModuleReaExplaurStatus]') {
              this.error.set(
                'Modification interdite !',
                `Les écouvillons ayant été envoyés, il n'est plus possible de
modifier l'inscription au module RÉA expl AUR.`,
                `Si le service indiqué n'est pas le bon, merci de nous
contacter en utilisant le formulaire de contact.`,
                false
              )
            } else if (err === '[ModuleReaSinkBadUF]') {
              this.error.set(
                'Modification interdite !',
                `Il est interdit de retirer de la surveillance une UF qui est
par ailleurs inscrite au protocole RÉA Sink.`,
                `Si vous souhaitez retirer cette UF de la surveillance, vous
devez au préalable modifier ou supprimer votre inscription au module RÉA Sink.`,
                false
              )
            } else {
              this.setError()
            }
          } else {
            vm.$notify({
              type: 'success',
              title: 'Succès !',
              content: `La surveillance de l'établissement
${vm.surveillance.establishment.name} a été mise à jour avec succès.`
            })
            this.editModal = false
          }
        }
      ).catch(e => {
        this.setError()
      })
    }
  },

  mounted: function () {
    this.refresh()
  },

  components: {
    'unit-list': unitList,
    'select-unit': selectUnit,
    'c-checkbox': checkbox,
    'sui-error-panel': suiErrorPanel
  }
}
</script>

<style lang="less">
.edit-modal {
  h3 {
    margin-bottom: 2rem;
    .label {
      padding: 0.2rem 0.5rem;
    }
  }

  h3:first-child {
    margin-top: 0rem;
  }

  .input-group {
    .label {
      font-size: 100%;
    }
  }

  .error-collapse-content {
    min-height: 9rem;
  }

  .uf-collapse-content {
    min-height: 37rem;
  }

  .protocols {
    .c-checkbox {
      margin-bottom: 0.5rem;
    }
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
}
</style>
